import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  Spinner,
  Alert,
  Container,
  Card,
  Form,
  Button,
  Modal,
} from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import HusariaHeader from "views/pages/components/HusariaHeader";
import { toggleDialplans } from "./dialplanHelpers";
const DialplanTable = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);
  const tableName = "v_dialplans";
  const title = "Dialplans";
  const parentName = "HUCS";
  const addRoute = "/admin/hucs/add/dialplan";
  const editBaseRoute = "/admin/hucs/edit/dialplan";
  const rowKey = "dialplan_uuid";
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleModal, setToggleModal] = useState(false);
  const canDelete = hasPermission(userData, PERMISSIONS.DELETE);
  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
  const navigate = useNavigate();
  const visibleColumns = [
    { key: "dialplan_name", label: "Name" },
    { key: "dialplan_number", label: "Number" },
    { key: "dialplan_context", label: "Context" },
    { key: "dialplan_order", label: "Order" },
    { key: "dialplan_enabled", label: "Enabled" },
    { key: "dialplan_description", label: "Description" },
  ];

  const fetchRows = async () => {
    setLoading(true);
    try {
      const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
      const site_uuid = currentSite?.site_uuid || null;
      const response = await fetch(
        `${serverHost}/get_rows?table=v_dialplans&pageSize=50&page=1&domain_uuid=${encodeURIComponent(
          site_uuid,
        )}`,
      );
      if (!response.ok) throw new Error(`Failed to fetch ${tableName}`);
      const data = await response.json();
      const row_data = data.results
      console.log(row_data)
      setRows(row_data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchRows();
  }, [tableName]);

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    selected: selectedRows,
    onSelect: (row, isSelect) => {
      setSelectedRows((prev) =>
        isSelect
          ? [...prev, row.dialplan_uuid]
          : prev.filter((id) => id !== row.dialplan_uuid),
      );
    },
    onSelectAll: (isSelect, rows) => {
      if (isSelect) {
        setSelectedRows(rows.map((r) => r.dialplan_uuid));
      } else {
        setSelectedRows([]);
      }
    },
    classes: "checkbox-column",
    headerClasses: "checkbox-column-header",
  };

  const handleToggleEnabled = async () => {
    if (selectedRows.length === 0) return;
    setSelectedRows([]);

    // Close the modal immediately
    setToggleModal(false);

    try {
      await toggleDialplans(selectedRows);

      // Refresh table and summary
      await fetchRows();
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const summary = await fetchRows(site?.site_uuid);
    } catch (err) {
      alert(err.message);
    }
  };

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell) => (
      <span style={{ fontWeight: index === 0 ? 600 : 400 }}>{cell ?? "-"}</span>
    ),
    headerAlign: "center",
    align: "center",
  }));

  columns.push({
    dataField: "actions",
    text: "Actions",
    headerAlign: "center",
    align: "center",
    formatter: (_, row) => (
      <Button
        variant="danger"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          toggleDeleteModal(row);
        }}
      >
        Delete
      </Button>
    ),
  });

  const rowEvents = {
    //app_uuid
    onClick: (e, row) => {
      if (canEdit) {
        console.log(row["is_dest"])
        if (row["is_dest"]) navigate(`/admin/hucs/edit/destination/${row[rowKey]}`); 
        else navigate(`${editBaseRoute}/${row[rowKey]}`);
      }
    },
  };

  const filteredRows = React.useMemo(() => {
    if (!search) return rows;

    return rows.filter((row) =>
      visibleColumns.some((col) =>
        String(row[col.key] ?? "")
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    );
  }, [rows, search, visibleColumns]);

  const toggleDeleteModal = (row = null) => {
    setRowToDelete(row);
    setDeleteModal(!deleteModal);
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    try {
      const response = await fetch(
        `${serverHost}/delete_route/${rowToDelete[rowKey]}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );
      if (!response.ok) throw new Error("Delete failed");
      await response.json();
      toggleDeleteModal();
      fetchRows();
    } catch (err) {
      alert(`Error deleting row: ${err.message}`);
    }
  };

  const singularTitle = title.endsWith("s") ? title.slice(0, -1) : title;

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading {title?.toLowerCase() || "data"}...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader name={title} parentName={parentName} padding="4.5rem" />

      <Container fluid className="mt--6">
        <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            <div className="d-flex align-items-center">

              <Form className="navbar-search navbar-search-light form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge search-border">
                    <InputGroupText>
                      <i
                        className="fas fa-search search-icon"
                        style={{
                          color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)",
                        }}
                      />
                    </InputGroupText>
                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>

            <div className="d-flex gap-2">
              {canEdit && selectedRows.length > 0 && (
                <Button
                  size="sm"
                  variant="info"
                  onClick={() => setToggleModal(true)}
                >
                  Toggle Enabled ({selectedRows.length})
                </Button>
              )}

              {canDelete && selectedRows.length > 0 && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setDeleteModal(true);
                  }}
                >
                  Delete Selected ({selectedRows.length})
                </Button>
              )}
              <Button variant="info" size="sm" onClick={fetchRows}>
                Refresh
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(addRoute)}
              >
                + Add {singularTitle}
              </Button>
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            <div style={{ overflowX: "auto" }}>
              <ToolkitProvider
                keyField={rowKey}
                data={filteredRows}
                columns={columns}
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    <BootstrapTable
                      {...props.baseProps}
                      bootstrap4
                      pagination={pagination}
                      selectRow={canDelete ? selectRow : undefined}
                      bordered
                      hover
                      rowEvents={rowEvents}
                      rowStyle={{ cursor: canEdit ? "pointer" : "default" }}
                      classes={`align-items-center table-flush ${isDark ? "table-dark" : ""}`}
                      noDataIndication={() =>
                        loading ? (
                          <div className="text-center">Loading...</div>
                        ) : (
                          <div className="text-center">No dialplans found.</div>
                        )
                      }
                    />
                  </div>
                )}
              </ToolkitProvider>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* ===================== TOGGLE ENABLED MODAL ===================== */}
      <Modal show={toggleModal} onHide={() => setToggleModal(false)}>
        <Modal.Header
          closeButton
          style={{ backgroundColor: isDark ? "#1f2937" : "#fff" }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            {selectedRows.length === 1
              ? "Toggle Dialplan"
              : "Toggle Dialplans"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          Are you sure you want to toggle{" "}
          <strong>
            {selectedRows.length === 1
              ? "this dialplan"
              : `${selectedRows.length} dialplans`}
          </strong>
          ?
        </Modal.Body>

        <Modal.Footer
          style={{ backgroundColor: isDark ? "#111827" : "#f8f9fa" }}
        >
          <Button variant="light" onClick={() => setToggleModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleToggleEnabled}>
            Toggle Enabled
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal} onHide={toggleDeleteModal}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
          }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            Delete {singularTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          Are you sure you want to delete this {singularTitle}? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#111827" : "#f8f9fa",
          }}
        >
          <Button variant="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DialplanTable;
